import { GuestInfo, GuestInfoDisplayMode } from '@models/guest-info.model';
import { TranslateModule } from '@ngx-translate/core';
import { format, isValid } from 'date-fns';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

@Component({
  selector: 'app-guest-info',
  standalone: true,
  imports: [CommonModule, TranslateModule, TooltipModule],
  template: `
    <div class="guest-info flex flex-col gap-1">
      <!-- Name, with optional inline markers projected next to it (e.g. booking remarks) -->
      <div class="flex items-center gap-1.5 font-medium">
        <span>{{ displayName }}</span>
        <ng-content select="[guestNameSuffix]" />
      </div>

      <!-- Email -->
      @if (showEmail && displayEmail) {
        <div class="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
          <i class="pi pi-envelope"></i>
          <span>{{ displayEmail }}</span>
        </div>
      }

      <!-- Phone -->
      @if (showPhone && displayPhone) {
        <div class="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
          <i class="pi pi-phone"></i>
          <span>{{ displayPhone }}</span>
        </div>
      }

      <!-- App Login (only in USER mode) -->
      @if (showAppLogin && displayMode === 'user' && guestInfo.lastAppLogin) {
        <div
          class="flex items-center gap-1 text-sm"
          [class]="getAppLoginClass(guestInfo.lastAppLogin)"
          [pTooltip]="'guestInfo.lastAppLogin' | translate"
        >
          <i class="pi pi-mobile"></i>
          <span>{{ formatAppLogin(guestInfo.lastAppLogin) }}</span>
        </div>
      }
    </div>
  `
})
export class GuestInfoComponent {
  @Input({ required: true }) guestInfo!: GuestInfo;
  @Input() showEmail: boolean = true;
  @Input() showPhone: boolean = true;
  @Input() showAppLogin: boolean = true;
  @Input() displayMode: GuestInfoDisplayMode = GuestInfoDisplayMode.USER;

  get displayName(): string {
    if (this.displayMode === GuestInfoDisplayMode.BOOKING) {
      return this.guestInfo.bookingName || this.guestInfo.name;
    }
    return this.guestInfo.name;
  }

  get displayEmail(): string | undefined {
    if (this.displayMode === GuestInfoDisplayMode.BOOKING) {
      return this.guestInfo.bookingEmail || this.guestInfo.email;
    }
    return this.guestInfo.email;
  }

  get displayPhone(): string | undefined {
    if (this.displayMode === GuestInfoDisplayMode.BOOKING) {
      return this.guestInfo.bookingPhone || this.guestInfo.phone;
    }
    return this.guestInfo.phone;
  }

  /**
   * Format app login datetime
   * @param date Date object
   * @returns Formatted date or relative time
   */
  formatAppLogin(date: Date): string {
    if (!date) return '';
    try {
      if (!isValid(date)) return '';

      return format(date, 'yyyy-MM-dd HH:mm');
    } catch (e) {
      return '';
    }
  }

  /**
   * Get CSS class based on app login recency
   * @param loginDate App login Date object
   * @returns CSS class for styling
   */
  getAppLoginClass(loginDate: Date): string {
    if (!loginDate) return 'text-gray-400';

    try {
      if (!isValid(loginDate)) return 'text-gray-400';

      const now = new Date();
      const diffDays = Math.floor((now.getTime() - loginDate.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays < 1) return 'text-green-600'; // Today
      if (diffDays < 3) return 'text-blue-600'; // Recent
      if (diffDays < 7) return 'text-orange-600'; // This week
      return 'text-gray-600'; // Older
    } catch (e) {
      return 'text-gray-400';
    }
  }
}
